import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ExternalLink, TrendingUp, TrendingDown, Users, Landmark, DollarSign } from "lucide-react";
import {
  LineChart, Line, AreaChart, Area, ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { endringsLinjer } from "@/components/endringMerke";
import { formatNumber, formatCurrency, formatYAxis, getMaxValue, dec } from "@/lib/formatters";

const KommuneCharts = ({ financeData, populationData, variant = "all" }) => {
  const incomeExpenseData = financeData.map((d) => ({
    year: d.year,
    Inntekter: d.inntekt,
    Utgifter: d.kostnad,
  }));

  // Debt = gjeld (KG103) + lån (KG100), keep separate values for tooltip
  // Also calculate gjeldsgrad (debt ratio) as percentage
  const debtAssetsData = financeData.map((d) => {
    const totalGjeld = (d.gjeld || 0) + (d.laan || 0);
    const gjeldsgrad = d.anleggsmidler && totalGjeld > 0
      ? ((totalGjeld / d.anleggsmidler) * 100)
      : null;
    return {
      year: d.year,
      Eigedelar: d.anleggsmidler,
      "Total gjeld": totalGjeld > 0 ? totalGjeld : null,
      Gjeldsgrad: gjeldsgrad,
      _gjeld: d.gjeld,  // For tooltip
      _laan: d.laan,    // For tooltip
    };
  });

  const populationChartData = populationData.map((d) => ({
    year: d.year,
    Befolkning: d.population,
  }));

  // Calculate max values for Y-axis scaling
  const maxIncomeExpense = getMaxValue(incomeExpenseData, ['Inntekter', 'Utgifter']);
  const maxDebtAssets = getMaxValue(debtAssetsData, ['Eigedelar', 'Total gjeld']);
  const maxPopulation = getMaxValue(populationChartData, ['Befolkning']);

  // Calculate population growth
  const currentPopulation = populationData.find(d => d.year === 2024)?.population;
  const futurePopulation = populationData.find(d => d.year === 2034)?.population;
  const populationGrowth = currentPopulation && futurePopulation 
    ? dec((((futurePopulation - currentPopulation) / currentPopulation) * 100), 2)
    : null;

  // Calculate metrics

  return (
    <>
        {variant !== "finance" && (
        <div className="grid grid-cols-1 gap-6 mb-8" id="framskriving">
          {/* Population Projection Chart */}
          <Card className="bg-white border border-[#E6E9F0] rounded-lg">
            <CardHeader className="border-b border-[#E6E9F0] pb-4">
              <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
                <Users className="w-5 h-5 text-[#103766]" />
                Framskriven befolkningsutvikling
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {populationChartData.length > 0 ? (
                <>
                  <div className="mb-4 p-4 bg-[#F6F8FB] rounded-lg">
                    <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">
                      Befolkningsvekst neste 10 år
                    </p>
                    <div className="flex items-center gap-2">
                      <p className="text-2xl font-light text-[#1F2A36]">
                        {populationGrowth ? `${populationGrowth}%` : "—"}
                      </p>
                      {populationGrowth && parseFloat(populationGrowth) >= 0 ? (
                        <TrendingUp className="w-5 h-5 text-[#3F8A86]" />
                      ) : (
                        <TrendingDown className="w-5 h-5 text-[#D35E4E]" />
                      )}
                    </div>
                    <p className="text-sm text-[#4B647F] mt-1">
                      Frå {formatNumber(currentPopulation)} til {formatNumber(futurePopulation)}
                    </p>
                  </div>
                  <div className="h-[250px]" data-testid="population-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={populationChartData}>
                        {endringsLinjer(populationChartData)}
                        <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                        <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} />
                        <YAxis 
                          stroke="#8AA4BF" 
                          fontSize={12} 
                          tickFormatter={(v) => formatYAxis(v, maxPopulation)} 
                        />
                        <Tooltip
                          formatter={(value) => [formatNumber(value), "Befolkning"]}
                          contentStyle={{
                            background: "white",
                            border: "1px solid #E6E9F0",
                            borderRadius: "8px",
                          }}
                        />
                        <Area
                          type="monotone"
                          dataKey="Befolkning"
                          stroke="#103766"
                          fill="#103766"
                          fillOpacity={0.3}
                          strokeWidth={2}
                          strokeDasharray="5 5"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-xs text-[#8AA4BF] mt-2 text-center">
                    Kjelde:{" "}
                    <a href="https://www.ssb.no/statbank/table/12882" target="_blank" rel="noopener noreferrer" className="text-[#103766] hover:underline inline-flex items-center gap-1">
                      SSB 12882 <ExternalLink className="w-3 h-3" />
                    </a>
                  </p>
                </>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-[#8AA4BF]">
                  Ingen befolkningsdata tilgjengeleg
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        )}
        {variant !== "population" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">

          {/* Income vs Expenses Chart */}
          <Card className="bg-white border border-[#E6E9F0] rounded-lg">
            <CardHeader className="border-b border-[#E6E9F0] pb-4">
              <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#103766]" />
                Driftsinntekter og utgifter
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="h-[300px]" data-testid="income-expense-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={incomeExpenseData}>
                    {endringsLinjer(incomeExpenseData)}
                    <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                    <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} />
                    <YAxis stroke="#8AA4BF" fontSize={12} tickFormatter={(v) => formatYAxis(v, maxIncomeExpense)} />
                    <Tooltip
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{
                        background: "white",
                        border: "1px solid #E6E9F0",
                        borderRadius: "8px",
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="Inntekter"
                      stroke="#103766"
                      strokeWidth={2}
                      dot={{ fill: "#103766", strokeWidth: 2 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="Utgifter"
                      stroke="#D35E4E"
                      strokeWidth={2}
                      dot={{ fill: "#D35E4E", strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="text-xs text-[#8AA4BF] mt-2 text-center">
                Kjelde:{" "}
                <a href="https://www.ssb.no/statbank/table/12858" target="_blank" rel="noopener noreferrer" className="text-[#103766] hover:underline inline-flex items-center gap-1">
                  SSB 12858 <ExternalLink className="w-3 h-3" />
                </a>
              </p>
            </CardContent>
          </Card>




          {/* Assets vs Debt Chart with Gjeldsgrad trend line */}
          <Card className="bg-white border border-[#E6E9F0] rounded-lg">
            <CardHeader className="border-b border-[#E6E9F0] pb-4">
              <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
                <Landmark className="w-5 h-5 text-[#103766]" />
                Gjeld og eigedelar
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="h-[300px]" data-testid="debt-assets-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={debtAssetsData}>
                    {endringsLinjer(debtAssetsData, { yAxisId: "left" })}
                    <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                    <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} />
                    <YAxis 
                      yAxisId="left"
                      stroke="#8AA4BF" 
                      fontSize={12} 
                      tickFormatter={(v) => formatYAxis(v, maxDebtAssets)} 
                    />
                    <YAxis 
                      yAxisId="right"
                      orientation="right"
                      stroke="#E8A34A" 
                      fontSize={12} 
                      tickFormatter={(v) => `${v.toFixed(0)}%`}
                      domain={[0, 'auto']}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0]?.payload;
                          return (
                            <div className="bg-white border border-[#E6E9F0] rounded-lg p-3 shadow-sm">
                              <p className="font-medium text-[#1F2A36] mb-2">{label}</p>
                              {payload.map((entry, index) => (
                                <p key={entry.name} style={{ color: entry.color }} className="text-sm">
                                  {entry.name}: {entry.name === "Gjeldsgrad" 
                                    ? `${dec(entry.value, 2)}%` 
                                    : `${formatNumber(entry.value)} tusen kr`}
                                </p>
                              ))}
                              {data?._gjeld != null && data?._laan != null && (
                                <div className="mt-2 pt-2 border-t border-[#E6E9F0] text-xs text-[#4B647F]">
                                  <p>Kortsiktig gjeld: {formatNumber(data._gjeld)}</p>
                                  <p>Langsiktig lån: {formatNumber(data._laan)}</p>
                                </div>
                              )}
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Legend />
                    <Bar yAxisId="left" dataKey="Eigedelar" fill="#3F8A86" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="left" dataKey="Total gjeld" fill="#D35E4E" radius={[4, 4, 0, 0]} />
                    <Line 
                      yAxisId="right"
                      type="monotone" 
                      dataKey="Gjeldsgrad" 
                      stroke="#E8A34A" 
                      strokeWidth={3}
                      dot={{ fill: "#E8A34A", strokeWidth: 2, r: 4 }}
                      name="Gjeldsgrad"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <p className="text-xs text-[#8AA4BF] mt-2 text-center">
                Kjelde:{" "}
                <a href="https://www.ssb.no/statbank/table/13545" target="_blank" rel="noopener noreferrer" className="text-[#103766] hover:underline inline-flex items-center gap-1">
                  SSB 13545 <ExternalLink className="w-3 h-3" />
                </a>
              </p>
            </CardContent>
          </Card>

        </div>
        )}

    </>
  );
};

export default KommuneCharts;
